// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Popover } from '@mantine/core';
import type { Organization } from '@medplum/fhirtypes';
import type { Cms1500Form, Cms1500Issue, Cms1500ProviderRole } from '@polarissleep/shared';
import { CMS1500_DATES_15_MORE, validateCms1500Form } from '@polarissleep/shared';
import { IconAlertCircle, IconCheck, IconChevronDown } from '@tabler/icons-react';
import type { JSX } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import type { ClaimValidationIssue } from './claimTasks';
import type { Cms1500EditorContextValue } from './cms1500/Cms1500Fields';
import { BoxNum, Cms1500EditorContext, classes, cx, setPath } from './cms1500/Cms1500Fields';
import {
  AddressRow,
  AttachmentResubmissionRow,
  ClinicalRow,
  CoverageRow,
  DiagnosisRow,
  InsuranceRow,
  NamesRow,
  NoteRow,
  PayerRow,
  ProvidersRow,
  SignatureRow,
  TotalsRow,
} from './cms1500/Cms1500Rows';
import { LINE_DETAIL_FIELDS, ServiceLinesRow } from './cms1500/Cms1500ServiceLines';

interface DraftClaimEditorProps {
  /** The CMS-1500 form stored on the Claim (or autofilled for a draft saved before forms existed). */
  initialForm: Cms1500Form;
  /** Payer Organizations that can be selected for this claim. */
  payers: Organization[];
  initialPayerOrganizationId?: string;
  readOnly?: boolean;
  /** Issues only the submit-claim bot can raise (self-pay coverage, missing encounter) — listed with the form's. */
  extraIssues?: ClaimValidationIssue[];
  saving?: boolean;
  submitting?: boolean;
  onSaveDraft: () => void;
  onSubmit: () => void;
}

export interface DraftClaimEditorHandle {
  getForm: () => Cms1500Form;
  getPayerOrganizationId: () => string | undefined;
  /** Show every validation error inline (and open the error list when there are any); returns them. */
  revealErrors: () => Cms1500Issue[];
  /** Reveal and focus the field behind an issue, opening whatever panel hides it. */
  focusField: (key: string) => void;
}

function focusClaimField(key: string): void {
  const target = document.querySelector<HTMLElement>(`[data-claim-field="${CSS.escape(key)}"]`);
  if (!target) {
    return;
  }
  const focusable = target.matches('input, select, textarea, button')
    ? target
    : target.querySelector<HTMLElement>('input:not([type="hidden"]), select, textarea, button');
  target.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  (focusable ?? target).focus?.();
}

/**
 * Draft claim editor laid out as Stedi's 1500 Professional Claim form. Every field writes to the
 * CMS-1500 form stored on the Claim, which is exactly what Submit sends to Stedi.
 */
export const DraftClaimEditor = forwardRef<DraftClaimEditorHandle, DraftClaimEditorProps>(function DraftClaimEditor(
  {
    initialForm,
    payers,
    initialPayerOrganizationId,
    readOnly = false,
    extraIssues = [],
    saving = false,
    submitting = false,
    onSaveDraft,
    onSubmit,
  },
  ref
): JSX.Element {
  const [form, setForm] = useState<Cms1500Form>(initialForm);
  const [payerOrganizationId, setPayerOrganizationId] = useState(initialPayerOrganizationId);
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const [reveal, setReveal] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [showAllDates, setShowAllDates] = useState(() =>
    CMS1500_DATES_15_MORE.some(([qualifier]) => Boolean(initialForm.dates[qualifier]))
  );
  const [providerTab, setProviderTab] = useState<Cms1500ProviderRole>('82');
  const [drawerLine, setDrawerLine] = useState<number | null>(null);

  const issues = useMemo(() => validateCms1500Form(form), [form]);
  const issueByKey = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const issue of issues) {
      if (!byKey.has(issue.key)) {
        byKey.set(issue.key, issue.message);
      }
    }
    return byKey;
  }, [issues]);
  const listed: { key: string; box: string; message: string }[] = [
    ...issues,
    ...extraIssues.map((issue) => ({ key: issue.field, box: issue.box ?? 'Claim', message: issue.message })),
  ];

  const update = useCallback((mutate: (draft: Cms1500Form) => void): void => {
    setForm((current) => {
      const draft = JSON.parse(JSON.stringify(current)) as Cms1500Form;
      mutate(draft);
      return draft;
    });
  }, []);
  const setValue = useCallback(
    (path: string, value: unknown): void => update((draft) => setPath(draft, path, value)),
    [update]
  );
  const touch = useCallback((path: string): void => {
    setTouched((current) => (current.has(path) ? current : new Set(current).add(path)));
  }, []);
  const errorFor = useCallback(
    (path: string): string | undefined => (reveal || touched.has(path) ? issueByKey.get(path) : undefined),
    [issueByKey, reveal, touched]
  );
  const visibleIssues = useMemo(() => {
    const visible = new Map<string, string>();
    for (const [key, message] of issueByKey) {
      if (reveal || touched.has(key)) {
        visible.set(key, message);
      }
    }
    return visible;
  }, [issueByKey, reveal, touched]);

  const focusField = (key: string): void => {
    setReveal(true);
    setErrorsOpen(false);
    const provider = /^prov\.(82|DN|DK|DQ)\./.exec(key);
    if (provider) {
      setProviderTab(provider[1] as Cms1500ProviderRole);
    }
    const line = /^lines\.(\d+)\.(\w+)/.exec(key);
    setDrawerLine(line && LINE_DETAIL_FIELDS.includes(line[2]) ? Number(line[1]) : null);
    if (CMS1500_DATES_15_MORE.some(([qualifier]) => key === `dates.${qualifier}`)) {
      setShowAllDates(true);
    }
    setTimeout(() => focusClaimField(key), 0);
  };
  const goToLines = (): void => focusClaimField('lines.0.from');

  useImperativeHandle(ref, () => ({
    getForm: () => form,
    getPayerOrganizationId: () => payerOrganizationId,
    revealErrors: () => {
      setReveal(true);
      if (issues.length) {
        setErrorsOpen(true);
      }
      return issues;
    },
    focusField,
  }));

  const context: Cms1500EditorContextValue = { form, readOnly, setValue, update, errorFor, touch };

  return (
    <Cms1500EditorContext.Provider value={context}>
      <section className={classes(cx.cx, readOnly && cx.ro)} aria-label="CMS-1500 claim form">
        <div className={cx.bar}>
          <div className={cx.crumb}>
            Submit claim<span>1500 Professional Claim</span>
          </div>
          <div className={cx.barRight}>
            {readOnly ? (
              <span className={cx.pill}>
                <IconCheck />
                Submitted — read-only
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className={classes(cx.btn, cx.btnSecondary)}
                  disabled={saving}
                  onClick={onSaveDraft}
                >
                  {saving ? 'Saving…' : 'Save draft'}
                </button>
                <Popover
                  opened={errorsOpen && listed.length > 0}
                  onChange={setErrorsOpen}
                  position="bottom-end"
                  width={440}
                  shadow="md"
                >
                  <Popover.Target>
                    <button
                      type="button"
                      className={classes(cx.pill, listed.length > 0 && cx.pillBad)}
                      disabled={!listed.length}
                      onClick={() => setErrorsOpen((open) => !open)}
                    >
                      {listed.length ? <IconAlertCircle /> : <IconCheck />}
                      {listed.length
                        ? `${listed.length} validation error${listed.length === 1 ? '' : 's'}`
                        : 'No validation errors'}
                      {listed.length > 0 && <IconChevronDown size={14} />}
                    </button>
                  </Popover.Target>
                  <Popover.Dropdown p={6}>
                    <div
                      className={cx.errs}
                      style={{ position: 'static', boxShadow: 'none', border: 0, width: 'auto' }}
                    >
                      <div className={cx.errsHead}>Fix these before submitting</div>
                      {listed.map((issue) => (
                        <button
                          key={`${issue.key}:${issue.message}`}
                          type="button"
                          onClick={() => focusField(issue.key)}
                        >
                          <BoxNum n={issue.box} />
                          <span>{issue.message}</span>
                        </button>
                      ))}
                    </div>
                  </Popover.Dropdown>
                </Popover>
                <button
                  type="button"
                  className={classes(cx.btn, cx.btnPrimary)}
                  disabled={submitting}
                  onClick={onSubmit}
                >
                  {submitting ? 'Submitting…' : 'Submit claim'}
                </button>
              </>
            )}
          </div>
        </div>
        <PayerRow
          payers={payers}
          payerOrganizationId={payerOrganizationId}
          onPayerOrganizationChange={(payer) => setPayerOrganizationId(payer.id)}
        />
        <InsuranceRow />
        <NamesRow />
        <AddressRow />
        <CoverageRow />
        <SignatureRow />
        <ClinicalRow
          showAllDates={showAllDates}
          onShowAllDatesChange={setShowAllDates}
          providerTab={providerTab}
          onProviderTabChange={setProviderTab}
        />
        <NoteRow onGoToLines={goToLines} />
        <AttachmentResubmissionRow />
        <DiagnosisRow onGoToLines={goToLines} />
        <ServiceLinesRow drawerLine={drawerLine} onDrawerLineChange={setDrawerLine} issueMessages={visibleIssues} />
        <TotalsRow />
        <ProvidersRow />
      </section>
    </Cms1500EditorContext.Provider>
  );
});
